import { h } from "preact";
import { useState, useCallback, useRef, useMemo, useEffect } from "preact/hooks";
import { ServiceManager } from "./ServiceManager";
import { ServiceInfo, defaultServices, defaultCharacteristics, CharacteristicInfo } from "./GATT";
import { CharacterisitcManager } from "./CharacteristicManager";
import { Service } from "./Service";
import { NoticeBlock } from "./NoticeBlock";
import { DiscoveredService } from "./DiscoveredService";
import { Characteristic } from "./Characteristic";

export function App() {
    const [status, setStatus] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [services, setServices] = useState<ServiceInfo[]>(defaultServices);
    const [characteristics, setCharacteristics] = useState<CharacteristicInfo[]>(defaultCharacteristics);
    const [connection, setConnection] = useState<BluetoothRemoteGATTServer | null>(null);
    const [discoveredServices, setDiscoveredServices] = useState<DiscoveredService[]>([]);
    const [showDefaultServices ,setShowDefaultServices] = useState(false);
    const [showDefaultCharacteristics, setShowDefaultCharacteristics] = useState(false);
    const [rememberedDevices, setRememberedDevices] = useState<BluetoothDevice[]>([]);
    const [configurationPopupVisible, setConfigurationPopupVisible] = useState(false);
    const appData = useRef<{
        isReconnecting: boolean,
        isDisconnected: boolean
    }>();
    appData.current = useMemo(() => ({
        isReconnecting: false,
        isDisconnected: true
    }), []);

    useEffect(() => {
        if ((navigator.bluetooth as any).getDevices) {
            (navigator.bluetooth as any).getDevices().then((devices: BluetoothDevice[]) => {
                setRememberedDevices(devices);
            });
        }
    }, []);

    const connect = useCallback(async (e?: Event, rememberedDevice?: BluetoothDevice) => {
        let device = rememberedDevice;
        setError("");

        if (!device) {
            try {
                device = await navigator.bluetooth.requestDevice({
                    acceptAllDevices: true,
                    optionalServices: services.map(s => /0x\d+/.test(s.uuid) ? parseInt(s.uuid.substr(2), 16) : s.uuid)
                });
            }
            catch(ex) {
                setStatus("");
                setError(`Cannot request device (Error: ${ex})`);
                console.error(ex);
                return;
            }
        }

        setStatus("Connecting...");
        appData.current!.isReconnecting = true;
        reconnect(device);
    }, [services]);

    const reconnect = useCallback(async (device: BluetoothDevice) => {
        let gattServer;

        try {
            gattServer = await device.gatt!.connect();
            setConnection(gattServer);
        }
        catch(ex) {
            if (appData.current!.isDisconnected) {
                return false;
            }

            setStatus("");
            setError("Cannot connect to the GATT server");
            console.error(ex);
            return false;
        }

        if (!appData.current!.isReconnecting) {
            return;
        }

        appData.current!.isDisconnected = false;
        appData.current!.isReconnecting = false;

        setStatus("Connected");

        device.ongattserverdisconnected = () => {
            if (appData.current!.isDisconnected) {
                return;
            }

            setStatus("Disconnected. Trying to reconnect.");
            appData.current!.isReconnecting = true;
            let attempt = 0;

            const callback = async () => {
                if (!appData.current!.isReconnecting || appData.current!.isDisconnected) {
                    return;
                }

                if (!(await reconnect(device))) {
                    if (!appData.current!.isReconnecting || appData.current!.isDisconnected) {
                        return;
                    }

                    attempt++;
                    setStatus(`Disconnected. Trying to reconnect. Attempt: ${attempt}`);
                    setTimeout(callback, 1000);
                }
            };

            setTimeout(callback, 0);
        };

        discover(undefined, gattServer);
        return true;
    }, []);

    const discover = useCallback(async (e?: Event, overrideConnect?: BluetoothRemoteGATTServer) => {
        const conn = overrideConnect ? overrideConnect : connection;

        if (!conn) {
            return;
        }

        setStatus("Discovering...");

        const foundServices: {
            service: BluetoothRemoteGATTService,
            characteristics: {
                characteristic: BluetoothRemoteGATTCharacteristic,
                descriptors: BluetoothRemoteGATTDescriptor[]
            }[]
        }[] = [];
        let services;

        try {
            setError("");
            services = await conn.getPrimaryServices();
        }
        catch(ex) {
            if (appData.current!.isDisconnected) {
                return;
            }

            setError("No services found");
            return;
        }

        for (let i = 0; i < services.length; i++) {
            const service = services[i];
            const foundService: {
                service: BluetoothRemoteGATTService,
                characteristics: {
                    characteristic: BluetoothRemoteGATTCharacteristic,
                    descriptors: BluetoothRemoteGATTDescriptor[]
                }[]
            } = { service, characteristics: [] };

            foundServices.push(foundService);

            let characteristics;

            try {
                characteristics = await foundService.service.getCharacteristics();
            }
            catch(ex) {
                if (appData.current!.isDisconnected) {
                    return;
                }

                continue;
            }

            for (let j = 0; j < characteristics.length; j++) {
                const characteristic = characteristics[j];
                let descriptors: BluetoothRemoteGATTDescriptor[] = [];

                try {
                    descriptors = await characteristic.getDescriptors();
                }
                catch(ex) {
                    if (appData.current!.isDisconnected) {
                        return;
                    }
                }

                foundService.characteristics.push({ characteristic, descriptors });
            }


            if (appData.current!.isDisconnected) {
                return;
            }

            setDiscoveredServices(foundServices.slice());
        }

        setStatus("Discovering completed");
    }, [connection]);

    const disconnect = useCallback(() => {
        if (!connection) {
            return;
        }

        delete connection.device.ongattserverdisconnected;
        appData.current!.isDisconnected = true;
        appData.current!.isReconnecting = false;
        connection.disconnect()
        setConnection(null);
        setDiscoveredServices([]);
        setError("");
        setStatus("Disconnected");
    }, [connection]);


    const clearServices = useCallback(() => {
        setDiscoveredServices([]);
    }, [connection]);


    return <div>
        <div class="top-bar" style={{ padding: "0px 5px" }}>
            <div style={{ flex: "1 1 0" }}>
                <div>
                    {connection ?
                        <div>
                            <button onClick={disconnect} style={{ marginRight: "10px", background: "#e74c3c" }}>
                                Disconnect
                            </button>
                            <button onClick={() => {
                                connection.disconnect();
                            }} style={{ marginRight: "10px", background: "#34495e" }}>
                                Reconnect
                            </button>
                            <button onClick={discover} style={{ marginRight: "10px", background: "#34495e" }}>
                                Re-Discover
                            </button>
                            <button onClick={clearServices} style={{ marginRight: "10px", background: "#34495e" }}>
                                Clear services
                            </button>
                        <span>Connected to: <span class="badge">{connection.device.name}</span></span>
                        </div>
                        :
                        <div>
                            <button onClick={connect}>Connect</button>
                            { rememberedDevices.length > 0 &&
                                <span>
                                    <span style={{ marginRight: "5px", marginLeft: "5px" }} >or</span>
                                    <select onChange={(e) => {
                                        if (rememberedDevices[parseInt(e.currentTarget.value, 10)]) {
                                            connect(undefined, rememberedDevices[parseInt(e.currentTarget.value, 10)])
                                        }
                                    }}>
                                        <option value={-1}>Select device to connect...</option>
                                        {rememberedDevices.map((device, i) => <option value={i}>{device.name}</option>)}
                                    </select>
                                </span>
                            }
                        </div>
                    }
                </div>
            </div>
            <div style={{ padding: "0px 10px" }}>
                <button title="Services and characteristics configuration" class="icon" onClick={() => setConfigurationPopupVisible(true)}>build</button>
            </div>
        </div>
        <div style={{ display: "flex", marginTop: "10px" }}>
            <NoticeBlock type="success">
                {status}
            </NoticeBlock>
            <NoticeBlock type="error">
                {error}
            </NoticeBlock>
        </div>
        <div style={{ marginTop: "10px", padding: "10px", margin: "10px", display: "inline-flex", flexDirection: "column" }} class="paper">
            <div>If you want to remember your device enable:</div>
            <div style={{ padding: "0px 10px" }}><code>chrome://flags/#enable-experimental-web-platform-features</code></div>
            <div style={{ padding: "0px 10px" }}><code>chrome://flags/#enable-web-bluetooth-new-permissions-backend</code></div>
        </div>
        <div style={{ marginTop: "10px", padding: "10px", margin: "10px", display: "inline-flex", flexDirection: "column" }} class="paper">
            <div style={{ fontSize: "12px" }}>
                Source code (Git): <a href="https://git.egzotech.com/NodeJS/BLEGattExplorer">https://git.egzotech.com/NodeJS/BLEGattExplorer</a>
            </div>
        </div>
        <div class={"popup" + (configurationPopupVisible ? " visible" : "")}>
            <div class="content" style={{ padding: "20px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ flex: "1 1 0", fontSize: "20px" }}>Services and characteristics configuration</div>
                    <button class="icon" onClick={() => setConfigurationPopupVisible(false)}>close</button>
                </div>
                <div style={{ marginTop: "10px", display: "flex" }}>
                    <div style={{ marginRight: "20px" }}>
                        <div style={{ display: "flex" }}>
                            <div style={{ marginRight: "20px", fontWeight: "bold", display: "flex", justifyContent: "center", alignItems: "center" }}>Service list</div>
                            <div style={{ verticalAlign: "middle" }}>
                                <label style={{ verticalAlign: "middle" }} for="show-default-services">Show default services</label>
                                <input
                                    id="show-default-services"
                                    style={{ verticalAlign: "middle" }}
                                    onChange={(e) => setShowDefaultServices(e.currentTarget.checked)}
                                    type="checkbox"/>
                            </div>
                        </div>
                        <ServiceManager showDefault={showDefaultServices} onChange={(services) => setServices(defaultServices.concat(services))}/>
                    </div>
                    <div>
                        <div style={{ display: "flex" }}>
                            <div style={{ marginRight: "20px", fontWeight: "bold", display: "flex", justifyContent: "center", alignItems: "center" }}>Characterisitc list</div>
                            <div style={{ verticalAlign: "middle" }}>
                                <label style={{ verticalAlign: "middle" }}  for="show-default-characterisitcs">Show default characterisitcs</label>
                                <input
                                    id="show-default-characterisitcs"
                                    style={{ verticalAlign: "middle" }}
                                    onChange={(e) => setShowDefaultCharacteristics(e.currentTarget.checked)}
                                    type="checkbox"/>
                            </div>
                        </div>
                        <CharacterisitcManager
                            showDefault={showDefaultCharacteristics}
                            onChange={(characteristics) => setCharacteristics(defaultCharacteristics.concat(characteristics))}/>
                    </div>
                </div>
                <NoticeBlock type="info" style={{ marginTop: "10px" }}>
                    Notice: After adding new custom service or characteristic you need to reconnect to the device to see changes.
                </NoticeBlock>
            </div>
        </div>
        {connection && discoveredServices.length > 0 &&
            <div>
                <div style={{ marginLeft: "10px", marginRight: "10px", fontSize: "20px", marginBottom: "10px" , borderBottom: "1px solid #2f2f2f" }}>
                    Discovered services
                </div>
                <ServiceList discoveredServices={discoveredServices} services={services} characteristics={characteristics}/>
                {/* <div>{discoveredServices.map(s => <Service services={services} characteristics={characteristics} service={s}/>)}</div> */}
            </div>
        }
    </div>;
}

function ServiceList(props: { discoveredServices: DiscoveredService[], services: ServiceInfo[], characteristics: CharacteristicInfo[] }) {
    const [selectedIndex, setSelectedIndex] = useState(-1);

    return <div style={{ display: "flex" }}>
        <div class="list">
            {props.discoveredServices.map((s, i) => {
                const relatedService = s.service.uuid.endsWith("-0000-1000-8000-00805f9b34fb")
                    ? props.services.find(se => se.uuid.toLowerCase() === "0x" + s.service.uuid.substr(4, 4))
                    : props.services.find(se => se.uuid.toLowerCase() === s.service.uuid);

                return <div class={"item" + (selectedIndex === i ? " selected" : "")} style={{ padding: "5px 10px" }}
                    onClick={() => setSelectedIndex(i)}>
                    <div>{relatedService ? relatedService.name : "(no name)"}</div>
                    <div style={{ fontSize: "10px", color: "#dfdfdf" }}>{s.service.uuid}</div>
                </div>;
            })}
        </div>
        <div style={{ flex: "1 1 0", padding: "10px" }}>
            {selectedIndex >= 0 && props.discoveredServices[selectedIndex].characteristics.map(c =>
                <Characteristic characteristic={c} characteristics={props.characteristics}/>
            )}
        </div>
    </div>
}
